/**
 * All permission keys used in the application.
 * These correspond to Frontegg permission key strings.
 */
export const PERMISSIONS = {
  // Tax Configuration
  TAX_CONFIG_READ: 'rch.read.tax.config',
  TAX_CONFIG_CREATE: 'rch.create.tax.config',
  TAX_CONFIG_UPDATE: 'rch.update.tax.config',
  TAX_CONFIG_DELETE: 'rch.delete.tax.config',

  // Tax Transactions
  TAX_TRANSACTION_READ: 'rch.read.tax.transaction',

  // Transaction Details
  TRANSACTION_DETAILS_READ: 'rch.read.transaction-details',
  TRANSACTION_DETAILS_UPDATE: 'rch.update.transaction-details',

  // Tax Injection
  TAX_INJECTION_CREATE: 'rch.create.tax.injection',
  TAX_INJECTION_ADMIN: 'rch.admin.tax.injection',

  // Tax Admin
  TAX_ADMIN_READ: 'rch.read.tax.admin',
  TAX_ADMIN_CREATE: 'rch.create.tax.admin',
  TAX_ADMIN_UPDATE: 'rch.update.tax.admin',
  TAX_ADMIN_DELETE: 'rch.delete.tax.admin',

  // Fraud Command Center
  FCC_READ: 'rch.read.fcc',
  FCC_UPDATE: 'rch.update.fcc',

  // NetSuite Settlement Holds
  NETSUITE_SETTLEMENT_READ: 'rch.read.netsuite.settlement',
  NETSUITE_SETTLEMENT_UPDATE: 'rch.update.netsuite.settlement',

  // Common Merchant - Shopify Stores
  SHOPIFY_STORE_READ: 'rch.read.common-merchant.shopify-store',
  SHOPIFY_STORE_UPDATE: 'rch.update.common-merchant.shopify-store',
  SHOPIFY_STORE_DELETE: 'rch.delete.common-merchant.shopify-store',

  // Shopify Payments
  SHOPIFY_PAYMENTS_READ: 'rch.read.shopify.payments.embedded',
  SHOPIFY_PAYMENTS_UPDATE: 'rch.update.shopify.payments.embedded',

  // Shopify Business Entity
  SHOPIFY_BUSINESS_ENTITY_UPDATE: 'rch.update.common-merchant.shopify-business-entity',

  // Shopify Bank Account
  SHOPIFY_BANK_ACCOUNT_UPDATE: 'rch.update.common-merchant.shopify-bank-account',

  // Shopify Fetch (ad-hoc data pull)
  SHOPIFY_FETCH_CREATE: 'rch.create.shopify.fetch',

  // Account Onboarding (AccountOnboardingManager role)
  ACCT_ONBOARDING_READ: 'rch.read.acct-lifecycle.onboarding',
  ACCT_ONBOARDING_CREATE: 'rch.create.acct-lifecycle.onboarding',
  ACCT_ONBOARDING_UPDATE: 'rch.update.acct-lifecycle.onboarding',
  ACCT_ONBOARDING_DELETE: 'rch.delete.acct-lifecycle.onboarding',

  // Drop-In merchants (common-merchant extensions webhook)
  DROPIN_MERCHANT_READ: 'rch.read.dropin.merch',
  DROPIN_MERCHANT_UPDATE: 'rch.update.dropin.merch',
  DROPIN_MERCHANT_DELETE: 'rch.delete.dropin.merch',

  // Drop-In integrators (auth-server). Merchant links and credentials are
  // written under the update authority.
  DROPIN_INTEGRATOR_READ: 'rch.read.dropin.intgr',
  DROPIN_INTEGRATOR_CREATE: 'rch.create.dropin.intgr',
  DROPIN_INTEGRATOR_UPDATE: 'rch.update.dropin.intgr',
  DROPIN_INTEGRATOR_DELETE: 'rch.delete.dropin.intgr',
} as const

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/**
 * Permission keys that partner and supplier users hold. They grant nothing in
 * Toolbox, so they live outside `PERMISSIONS`: a user holding only these is
 * sent out of Toolbox (see `partnerSupplierRedirectTarget`).
 */
export const PARTNER_SUPPLIER_PERMISSIONS = {
  // Supplier access to the account app (ext-acct-lifecycle-mgmt-ui)
  SUPPLIER: 'rch.access.app.acct-lifecycle-mgmt',
  // Partner access to the partner account API
  PARTNER: 'rch.access.partner-account-api',
} as const

export type PartnerSupplierPermissionKey =
  (typeof PARTNER_SUPPLIER_PERMISSIONS)[keyof typeof PARTNER_SUPPLIER_PERMISSIONS]
